import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async'; // Import Helmet untuk SEO
import { FaThermometerHalf, FaTint, FaHome, FaSeedling, FaWarehouse, FaServer, FaCogs, FaChartLine, FaHistory, FaBell, FaRobot } from 'react-icons/fa'; // Ikon baru
import { MdOutlineIntegrationInstructions } from 'react-icons/md'; // Ikon untuk integrasi

// --- Import Recharts Components ---
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// --- CATATAN: TIDAK ADA IMPORT GAMBAR DARI src/assets LAGI DI SINI ---
// Gambar sekarang diakses langsung dari public/images/suhu_kelembaban/

// --- KONSTANTA UNTUK JUMLAH RAK SERVER ---
const NUM_SERVER_RACKS = 3; // Misalnya, kita pantau 3 rak server

function SensorSuhuKelembaban() {
  // Data simulasi untuk setiap rak server
  const [rackData, setRackData] = useState(() => {
    const initialData = [];
    const initialTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    for (let i = 0; i < 10; i++) { // Inisialisasi dengan beberapa titik data awal
      const dataPoint = { time: initialTime };
      for (let j = 0; j < NUM_SERVER_RACKS; j++) {
        dataPoint[`Suhu_Rack_${String.fromCharCode(65 + j)}`] = parseFloat((Math.random() * (27 - 23) + 23).toFixed(1));
        dataPoint[`Kelembaban_Rack_${String.fromCharCode(65 + j)}`] = parseFloat((Math.random() * (65 - 55) + 55).toFixed(0));
      }
      initialData.push(dataPoint);
    }
    return initialData;
  });

  useEffect(() => {
    // Fungsi simulasi data real-time untuk banyak rak
    const interval = setInterval(() => {
      setRackData(prev => {
        const newTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const newDataPoint = { time: newTime };
        for (let i = 0; i < NUM_SERVER_RACKS; i++) {
          newDataPoint[`Suhu_Rack_${String.fromCharCode(65 + i)}`] = parseFloat((Math.random() * (27 - 23) + 23).toFixed(1));
          newDataPoint[`Kelembaban_Rack_${String.fromCharCode(65 + i)}`] = parseFloat((Math.random() * (65 - 55) + 55).toFixed(0));
        }
        // Hanya menyimpan 10 data terakhir untuk grafik yang tidak terlalu padat
        return [...prev.slice(-9), newDataPoint];
      });
    }, 5000); // Update setiap 5 detik
    return () => clearInterval(interval); // Cleanup interval saat komponen di-unmount
  }, []); // Dependensi kosong agar hanya berjalan sekali saat mount dan update state di dalam callback

  // Palet warna untuk garis grafik yang berbeda
  const tempColors = ['#FF0000', '#0000FF', '#FFA500', '#800080', '#008080']; // Merah, Biru, Oranye, Ungu, Teal
  const humidityColors = ['#00FF00', '#FFD700', '#00FFFF', '#FFC0CB', '#8A2BE2']; // Hijau, Emas, Cyan, Pink, Biru-Ungu


  return (
    <>
      <Helmet>
        <title>Sensor Suhu & Kelembaban - Solusi IoT Cerdas | Monitor Lingkungan Cerdas</title>
        <meta name="description" content="Dapatkan data suhu dan kelembaban real-time dari berbagai rak server dengan sensor Solusi IoT Cerdas. Solusi pemantauan lingkungan akurat untuk rumah pintar, pertanian, dan industri." />
        <meta name="keywords" content="sensor suhu, sensor kelembaban, DHT11, DHT22, IoT, pemantauan lingkungan, smart home, pertanian cerdas, Solusi IoT Cerdas, monitor lingkungan, data real-time, rak server, data center, pemantauan server" />
        <link rel="canonical" href="https://www.Solusi IoT Cerdas.com/produk/sensor/suhu-kelembaban" />
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Sensor Suhu & Kelembaban - Solusi IoT Cerdas | Monitor Lingkungan Cerdas" />
        <meta property="og:description" content="Dapatkan data suhu dan kelembaban real-time dari berbagai rak server dengan sensor Solusi IoT Cerdas. Solusi pemantauan lingkungan akurat untuk rumah pintar, pertanian, dan industri." />
        <meta property="og:url" content="https://www.Solusi IoT Cerdas.com/produk/sensor/suhu-kelembaban" />
        <meta property="og:image" content="https://www.Solusi IoT Cerdas.com/images/sensor-suhu-kelembaban-og.jpg" /> {/* Ganti dengan URL gambar OG Anda */}
        <meta property="og:type" content="website" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sensor Suhu & Kelembaban - Solusi IoT Cerdas | Monitor Lingkungan Cerdas" />
        <meta name="twitter:description" content="Dapatkan data suhu dan kelembaban real-time dari berbagai rak server dengan sensor Solusi IoT Cerdas. Solusi pemantauan lingkungan akurat untuk rumah pintar, pertanian, dan industri." />
        <meta name="twitter:image" content="https://www.Solusi IoT Cerdas.com/images/sensor-suhu-kelembaban-twitter.jpg" /> {/* Ganti dengan URL gambar Twitter Anda */}
      </Helmet>

      {/* Kontainer utama dengan tema terang/gelap */}
      <div className="container mx-auto p-4 md:p-8 bg-gray-100 text-gray-800 font-sans dark:bg-gray-900 dark:text-gray-200 transition-colors duration-500">
        
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-teal-500 text-white rounded-xl shadow-xl p-8 md:p-12 mb-12 text-center transform transition-all duration-700 ease-in-out hover:shadow-2xl hover:scale-[1.005]
                            dark:from-blue-800 dark:to-teal-700">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4 animate-fade-in-down">
            Ukur Dunia Anda: Sensor Suhu & Kelembaban Akurat dari Solusi IoT Cerdas
          </h1>
          <p className="text-lg md:text-xl max-w-4xl mx-auto mb-8 opacity-90 animate-fade-in delay-200">
            Dapatkan pemahaman mendalam tentang lingkungan sekitar Anda dengan data suhu dan kelembaban <strong>*real-time*</strong> yang presisi. Ideal untuk rumah, pertanian, hingga kebutuhan industri.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6 mt-10">
            {/* Kartu Hero Section: Data Suhu (menampilkan suhu Rack A sebagai contoh utama) */}
            <div className="bg-white text-gray-900 rounded-lg p-7 shadow-lg flex-1 md:max-w-xs transition-transform duration-300 hover:scale-105 animate-slide-in-left
                                dark:bg-gray-800 dark:text-gray-100">
              <FaThermometerHalf size={48} className="text-blue-500 dark:text-blue-300 mx-auto mb-3" />
              <h3 className="text-xl font-semibold mb-2">Suhu Saat Ini (Rack A)</h3>
              <h2 className="text-5xl font-bold text-blue-600 dark:text-blue-400">
                {rackData[rackData.length - 1]?.[`Suhu_Rack_${String.fromCharCode(65 + 0)}`] || 'N/A'}°C
              </h2>
              <p className="text-sm text-gray-600 mt-3 dark:text-gray-400">Terakhir: {rackData[rackData.length - 1]?.time || 'N/A'}</p>
            </div>
            {/* Kartu Hero Section: Data Kelembaban (menampilkan kelembaban Rack A sebagai contoh utama) */}
            <div className="bg-white text-gray-900 rounded-lg p-7 shadow-lg flex-1 md:max-w-xs transition-transform duration-300 hover:scale-105 animate-slide-in-right delay-100
                                dark:bg-gray-800 dark:text-gray-100">
              <FaTint size={48} className="text-green-500 dark:text-green-300 mx-auto mb-3" />
              <h3 className="text-xl font-semibold mb-2">Kelembaban Saat Ini (Rack A)</h3>
              <h2 className="text-5xl font-bold text-green-600 dark:text-green-400">
                {rackData[rackData.length - 1]?.[`Kelembaban_Rack_${String.fromCharCode(65 + 0)}`] || 'N/A'}%
              </h2>
              <p className="text-sm text-gray-600 mt-3 dark:text-gray-400">Terakhir: {rackData[rackData.length - 1]?.time || 'N/A'}</p>
            </div>
          </div>
        </section>

        {/* Real-time Monitoring Graph Section */}
        <section className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-12 transform transition-all duration-500 hover:shadow-2xl
                            dark:bg-gray-800">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-8 dark:text-gray-100">
            Pantau Kondisi Berbagai Rak Server Anda Secara Real-time!
          </h2>
          <p className="text-lg text-gray-700 text-center mb-10 max-w-4xl mx-auto dark:text-gray-300">
            Dapatkan gambaran komprehensif tentang suhu dan kelembaban di berbagai rak server di fasilitas Anda. Setiap garis mewakili data dari rak server yang berbeda, diperbarui secara langsung.
          </p>
          <div className="w-full h-96 md:h-[500px]"> {/* Kontainer grafik dengan tinggi lebih besar */}
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={rackData}
                margin={{
                  top: 5, right: 30, left: 20, bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={document.documentElement.classList.contains('dark') ? '#444' : '#e0e0e0'} />
                <XAxis dataKey="time" stroke={document.documentElement.classList.contains('dark') ? '#ccc' : '#555'} />
                <YAxis yAxisId="temp" stroke="#8884d8" label={{ value: 'Suhu (°C)', angle: -90, position: 'insideLeft', fill: document.documentElement.classList.contains('dark') ? '#ccc' : '#555' }} />
                <YAxis yAxisId="humidity" orientation="right" stroke="#82ca9d" label={{ value: 'Kelembaban (%)', angle: 90, position: 'insideRight', fill: document.documentElement.classList.contains('dark') ? '#ccc' : '#555' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: document.documentElement.classList.contains('dark') ? '#333' : '#fff', border: '1px solid #ccc', borderRadius: '5px' }}
                  labelStyle={{ color: document.documentElement.classList.contains('dark') ? '#eee' : '#333' }}
                  itemStyle={{ color: document.documentElement.classList.contains('dark') ? '#eee' : '#333' }}
                />
                <Legend wrapperStyle={{ color: document.documentElement.classList.contains('dark') ? '#eee' : '#333', marginTop: '10px' }} />
                
                {/* Render garis untuk setiap rak server */}
                {Array.from({ length: NUM_SERVER_RACKS }).map((_, i) => (
                  <Line 
                    key={`temp_rack_${i}`}
                    yAxisId="temp"
                    type="monotone"
                    dataKey={`Suhu_Rack_${String.fromCharCode(65 + i)}`}
                    stroke={tempColors[i % tempColors.length]} // Ganti warna berdasarkan indeks
                    activeDot={{ r: 6 }}
                    name={`Suhu Rack ${String.fromCharCode(65 + i)}`}
                  />
                ))}
                {Array.from({ length: NUM_SERVER_RACKS }).map((_, i) => (
                  <Line 
                    key={`humidity_rack_${i}`}
                    yAxisId="humidity"
                    type="monotone"
                    dataKey={`Kelembaban_Rack_${String.fromCharCode(65 + i)}`}
                    stroke={humidityColors[i % humidityColors.length]} // Ganti warna berdasarkan indeks
                    activeDot={{ r: 6 }}
                    name={`Kelembaban Rack ${String.fromCharCode(65 + i)}`}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>


        {/* Kenapa Penting? */}
        <section className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-12 transform transition-all duration-500 hover:shadow-2xl
                            dark:bg-gray-800">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-8 dark:text-gray-100">
            Mengapa Memantau Suhu & Kelembaban Itu Sangat Penting?
          </h2>
          <p className="text-lg text-gray-700 text-center mb-10 max-w-4xl mx-auto dark:text-gray-300">
            Suhu dan kelembaban bukan sekadar angka, lho! Keduanya adalah faktor kunci yang berpengaruh besar pada kenyamanan, kesehatan, bahkan umur panjang aset-aset berharga Anda.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700 dark:text-gray-200">
              <FaHome size={36} className="text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-blue-700 mb-2 dark:text-blue-400">Kenyamanan Maksimal</h3>
              <p className="text-gray-600 dark:text-gray-300">Atur suhu dan kelembaban ideal untuk suasana rumah atau kantor yang nyaman sepanjang hari.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700 dark:text-gray-200">
              <FaSeedling size={36} className="text-green-600 dark:text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-green-700 mb-2 dark:text-green-400">Jaga Kesehatan Anda</h3>
              <p className="text-gray-600 dark:text-gray-300">Hindari jamur, alergi, dan masalah pernapasan yang disebabkan oleh kelembaban ekstrem.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700 dark:text-gray-200">
              <FaWarehouse size={36} className="text-purple-600 dark:text-purple-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Lindungi Aset Berharga</h3>
              <p className="text-gray-600 dark:text-gray-300">Amankan barang elektronik, dokumen penting, atau koleksi dari kerusakan akibat fluktuasi lingkungan.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700 dark:text-gray-200">
              <FaCogs size={36} className="text-orange-600 dark:text-orange-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-orange-700 mb-2 dark:text-orange-400">Optimalkan Efisiensi Energi</h3>
              <p className="text-gray-600 dark:text-gray-300">Pantau kondisi untuk mengatur AC atau pemanas, hemat biaya listrik Anda!</p>
            </div>
          </div>
        </section>

        {/* Fitur Unggulan Sensor Solusi IoT Cerdas */}
        <section className="bg-gray-50 rounded-xl shadow-lg p-8 md:p-12 mb-12 transform transition-all duration-500 hover:shadow-2xl
                            dark:bg-gray-950">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-8 dark:text-gray-100">
            Fitur Unggulan Sensor Suhu & Kelembaban Solusi IoT Cerdas
          </h2>
          <p className="text-lg text-gray-700 text-center mb-10 max-w-4xl mx-auto dark:text-gray-300">
            Solusi IoT Cerdas dirancang untuk memberikan kemudahan dan akurasi, bekerja mulus dengan sensor populer seperti seri <strong>DHT (DHT11 dan DHT22)</strong>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500 flex items-center space-x-4
                                transition-transform duration-300 hover:scale-105 hover:shadow-xl
                                dark:bg-gray-800 dark:border-blue-700 dark:text-gray-100">
              <MdOutlineIntegrationInstructions size={32} className="text-blue-500 dark:text-blue-300 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1 dark:text-gray-100">Integrasi Mudah</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  Pasang dan operasikan dalam waktu singkat, tidak perlu keahlian coding rumit.
                </p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500 flex items-center space-x-4
                                transition-transform duration-300 hover:scale-105 hover:shadow-xl
                                dark:bg-gray-800 dark:border-green-700 dark:text-gray-100">
              <FaChartLine size={32} className="text-green-500 dark:text-green-300 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1 dark:text-gray-100">Visualisasi Data Intuitif</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  Lihat data suhu dan kelembaban dalam grafik yang mudah dipahami secara <strong>*real-time*</strong>.
                </p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500 flex items-center space-x-4
                                transition-transform duration-300 hover:scale-105 hover:shadow-xl
                                dark:bg-gray-800 dark:border-purple-700 dark:text-gray-100">
              <FaHistory size={32} className="text-purple-500 dark:text-purple-300 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1 dark:text-gray-100">Riwayat Data Lengkap</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  Akses data historis untuk analisis tren dan pengambilan keputusan yang lebih baik.
                </p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500 flex items-center space-x-4
                                transition-transform duration-300 hover:scale-105 hover:shadow-xl
                                dark:bg-gray-800 dark:border-yellow-700 dark:text-gray-100">
              <FaBell size={32} className="text-yellow-500 dark:text-yellow-300 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1 dark:text-gray-100">Notifikasi Cerdas</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  Dapatkan peringatan instan jika suhu atau kelembaban melampaui batas yang Anda tentukan.
                </p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500 flex items-center space-x-4 col-span-1 md:col-span-2
                                transition-transform duration-300 hover:scale-105 hover:shadow-xl
                                dark:bg-gray-800 dark:border-red-700 dark:text-gray-100">
              <FaRobot size={32} className="text-red-500 dark:text-red-300 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1 dark:text-gray-100">Otomasi Pintar</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">
                  Gunakan data sensor sebagai pemicu otomatis untuk mengendalikan perangkat lain (AC, kipas, humidifier).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Aplikasi Dunia Nyata */}
        <section className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-12 transform transition-all duration-500 hover:shadow-2xl
                            dark:bg-gray-800">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-8 dark:text-gray-100">
            Beragam Aplikasi untuk Berbagai Kebutuhan Anda
          </h2>
          <p className="text-lg text-gray-700 text-center mb-10 max-w-4xl mx-auto dark:text-gray-300">
            Sensor suhu dan kelembaban Solusi IoT Cerdas sangat fleksibel dan bisa diterapkan di berbagai sektor, membantu Anda mencapai tujuan spesifik.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700">
              {/* Gambar untuk analogi "susu" (Galon 19L) - Path disesuaikan */}
              <img src="/images/suhu_kelembaban/Smart Home & Family Comfort.webp" alt="Pemantauan Kondisi Produk Sensitif" className="mx-auto mb-4 rounded-full w-24 h-24 object-cover" />
              <h3 className="text-xl font-semibold text-blue-700 mb-2 dark:text-blue-400">Rumah Pintar & Kenyamanan Keluarga</h3>
              <p className="text-gray-600 dark:text-gray-300">Pantau dan kendalikan suhu kamar bayi, kelembaban lemari pakaian, serta lingkungan rumah Anda secara real-time dengan sensor IoT untuk kenyamanan dan kesehatan keluarga.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700">
              {/* Gambar untuk Pertanian Presisi - Path disesuaikan */}
              <img src="/images/suhu_kelembaban/Smart Greenhouse & Precision Agriculture.webp" alt="Gambar Aplikasi Pertanian Presisi" className="mx-auto mb-4 rounded-full w-24 h-24 object-cover" />
              <h3 className="text-xl font-semibold text-blue-700 mb-2 dark:text-blue-400">Pertanian Presisi & Greenhouse Modern</h3>
              <p className="text-gray-600 dark:text-gray-300">Jaga kondisi mikroklimat yang optimal untuk pertumbuhan tanaman di kebun atau *greenhouse*, hindari gagal panen akibat cuaca ekstrem.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700">
              {/* Gambar untuk Penyimpanan & Logistik Sensitif - Path disesuaikan */}
              <img src="/images/suhu_kelembaban/Sensitive Logistics & Smart Storage.webp" alt="Gambar Aplikasi Penyimpanan & Logistik" className="mx-auto mb-4 rounded-full w-24 h-24 object-cover" />
              <h3 className="text-xl font-semibold text-blue-700 mb-2 dark:text-blue-400">Penyimpanan & Logistik Aman</h3>
              <p className="text-gray-600 dark:text-gray-300">Pastikan produk-produk sensitif seperti obat-obatan atau barang seni disimpan dan diangkut dalam kondisi suhu dan kelembaban yang tepat.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2
                                dark:bg-gray-700">
              {/* Gambar untuk Ruang Server - Path disesuaikan */}
              <img src="/images/suhu_kelembaban/Secure Server Room & Smart Data Center.webp" alt="Gambar Aplikasi Ruang Server" className="mx-auto mb-4 rounded-full w-24 h-24 object-cover" />
              <h3 className="text-xl font-semibold text-blue-700 mb-2 dark:text-blue-400">Pemantauan Server Solusi IoT & Pemetaan Pasti</h3>
              <p className="text-gray-600 dark:text-gray-300">Pantau suhu dan kelembaban secara konstan di ruang server penting seperti milik Solusi IoT Cerdas dan Solusi Pemetaan Pasti, untuk mencegah *overheating* dan memastikan kinerja optimal peralatan kritis.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-teal-600 to-blue-700 text-white rounded-xl shadow-xl p-8 md:p-12 text-center transform transition-all duration-500 hover:shadow-2xl hover:scale-[1.005]
                            dark:from-teal-800 dark:to-blue-900">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Siap Mengendalikan Lingkungan Anda?</h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto opacity-90">
            Jangan biarkan fluktuasi suhu dan kelembaban mengganggu kenyamanan atau bisnis Anda. Mulai pantau dengan sensor Solusi IoT Cerdas sekarang!
          </p>
          <button className="bg-white text-teal-700 font-bold py-3 px-8 rounded-full text-xl shadow-lg
                               transition duration-300 ease-in-out transform hover:scale-105 hover:bg-gray-100
                               dark:bg-blue-200 dark:text-blue-900 dark:hover:bg-blue-300"
                  onClick={() => alert('Fitur belajar lebih lanjut atau daftar!')}>
            Dapatkan Solusi Anda Hari Ini!
          </button>
        </section>
      </div>
    </>
  );
}

export default SensorSuhuKelembaban;